/**
 * Canonical validation rules for operational-data percentage splits.
 *
 * These rules originate from the manual operational-data input form
 * (`src/ui/common/generic/components/operational-data-input/shared/validation.ts`).
 * They were lifted here into `@upright/common` (PLT-5088) so the SAME rules can be enforced
 * both on the frontend and server-side at commit — the agentic upload flow validates the
 * applied batch on the backend before writing, and the chat card validates client-side.
 *
 * Rules (mirrors the manual form exactly):
 * - Block (error) when any individual percentage is negative.
 * - Block (error) when any individual percentage exceeds 100%.
 * - Block (error) when the total exceeds 100%.
 * - Warn (still saveable) when the total is under 50% — results are not auditable below that.
 *   EXCEPTION: the suppliers-by-industry split (SUPPLIERS × INDUSTRY) is intentionally only a
 *   subset of the high-impact industries suppliers belong to, so a sub-50% total is expected and
 *   NOT flagged (PLT-5088). The warning is skipped for that {metric, dimension} scope.
 * - There is intentionally NO sum-to-exactly-100% requirement, so deliberately partial
 *   datasets commit cleanly (they only trip the <50% auditability warning).
 * - Null values are treated as 0 during validation.
 */
import { CompanyOperationsDimension, CompanyOperationsMetricType } from './types';

export interface OperationalDataValidationResult {
  isValid: boolean;
  canSave: boolean; // Can save even with warnings
  warnings: string[];
  errors: string[];
}

/**
 * The {metric, dimension} a split belongs to. Optional on the validators — when omitted, the
 * <50% auditability warning applies as before. It is only consulted to suppress that warning for
 * the suppliers-by-high-impact-industry subset, where a partial total is by design.
 */
export interface OperationalDataValidationScope {
  operationsMetricType?: CompanyOperationsMetricType;
  dimension?: CompanyOperationsDimension;
}

/**
 * Suppliers split by industry is a deliberate subset of the high-impact industries suppliers
 * belong to — there is no expectation of covering 100% (or even 50%), so the auditability floor
 * does not apply to it (PLT-5088).
 */
function isHighImpactIndustrySubset(scope?: OperationalDataValidationScope): boolean {
  return (
    scope?.operationsMetricType === CompanyOperationsMetricType.SUPPLIERS &&
    scope.dimension === CompanyOperationsDimension.INDUSTRY
  );
}

/**
 * Slack allowed on the 100% total. Weights travel as fractions and are scaled back to percentages
 * here, and that round-trip is not exact in floating point: 14% + 55% + 31% sums to
 * 100.00000000000001. The tolerance is far below any precision a user can enter.
 */
const TOTAL_PERCENTAGE_TOLERANCE = 1e-6;

export function validatePercentageData(
  data: Array<{ percentage: number | null }>,
  scope?: OperationalDataValidationScope,
): OperationalDataValidationResult {
  const warnings: string[] = [];
  const errors: string[] = [];

  const hasNegative = data.some(row => (row.percentage ?? 0) < 0);
  if (hasNegative) {
    errors.push('Percentage values cannot be negative');
  }

  const hasOverHundred = data.some(row => (row.percentage ?? 0) > 100);
  if (hasOverHundred) {
    errors.push('Individual percentage cannot exceed 100%');
  }

  const sum = data.reduce((acc, row) => acc + (row.percentage ?? 0), 0);

  if (sum > 100 + TOTAL_PERCENTAGE_TOLERANCE) {
    errors.push(`Total percentage is ${sum.toFixed(1)}%. Total percentage cannot exceed 100%`);
  } else if (sum < 50 && !isHighImpactIndustrySubset(scope)) {
    warnings.push(
      `Total percentage is ${sum.toFixed(1)}%. Results are not auditable if less than 50% of splits are defined.`,
    );
  }

  return {
    isValid: errors.length === 0,
    canSave: errors.length === 0, // Only block if there are errors
    warnings,
    errors,
  };
}

/**
 * Validate operational-data rows whose weights are stored as fractions in `[0, 1]`
 * (the on-the-wire / DB representation), by converting to percentages first and applying
 * the canonical `validatePercentageData` rules. Used server-side by the batch-apply endpoint.
 */
export function validateOperationalDataWeights(
  rows: Array<{ weight: number | null }>,
  scope?: OperationalDataValidationScope,
): OperationalDataValidationResult {
  return validatePercentageData(
    rows.map(r => ({ percentage: r.weight == null ? null : r.weight * 100 })),
    scope,
  );
}
